import {toast} from '@wandb/weave/common/components/elements/Toast';
// @todo: Migrate to @wandb/components. For more details see:
// https://www.notion.so/wandbai/Migrating-components-207e2f5c7ef380129201f72a5770a8db
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components/Button';
import * as Dialog from '@wandb/weave/components/Dialog';
import {Select} from '@wandb/weave/components/Form/Select';
import {Icon} from '@wandb/weave/components/Icon';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import copyToClipboard from 'copy-to-clipboard';
import React, {useContext, useState} from 'react';
import {useHistory} from 'react-router-dom';

import {useViewer} from '../../../state/viewer/hooks';
import {
  PremiumFeatureTooltip,
  PremiumFeatureToolTipText,
  useIsAutomationEnabled,
} from '../../../util/accounts/pricing';
import {useRampFlagStarredRegistries} from '../../../util/rampFeatureFlags';
import {capitalizeFirstLetter} from '../../../util/string';
import * as urls from '../../../util/urls';
import {
  Banner,
  BannerBoldText,
  BannerFlexWrapper,
  BannerTextWrapper,
} from '../../Banner';
import {AccountSelectorContext} from '../../Search/SearchNav/AccountSelectorContextProvider';
import {useIsProjectReadOnly} from '../common/hooks';
import {StarredRegistriesContext} from '../common/StarredRegistriesContextProvider';
import {globalRegistryType} from '../common/utils';
import {MigrationInfoBanner} from '../ModelRegistryMigration/MigratedRegistryBanners/MigrationInfoBanner';
import {SDKVersionWarningBanner} from '../ModelRegistryMigration/MigratedRegistryBanners/SDKVersionWarningBanner';
import {
  lowerSdkVersion,
  sdkVersionsTab,
  useSDKVersion,
} from './SDKVersionContext';

const linkArtifactCodeSnippet = (
  entityName: string,
  registryProjectName: string,
  version: string
) => {
  if (version === lowerSdkVersion) {
    return `run.link_artifact(artifact, f"${entityName}/${registryProjectName}/{<insert_collection_name>}")`;
  }
  return `run.link_artifact(artifact, f"${registryProjectName}/{<insert_collection_name>}")`;
};

type RegistryPageHeaderProps = {
  registry: globalRegistryType;
  onCreateCollection?: () => void;
  onCreateAutomation?: () => void;
  showJoinBanner?: boolean;
};
export const RegistryPageHeader = ({
  registry,
  onCreateCollection,
  onCreateAutomation,
  showJoinBanner,
}: RegistryPageHeaderProps) => {
  const {
    orgId,
    orgName,
    entityName,
    registryName,
    registryProjectName,
    description,
  } = registry;
  const {isProjectReadOnly: readOnly} = useIsProjectReadOnly(
    entityName,
    registryProjectName
  );
  const viewer = useViewer();
  const isLoggedIn = !!viewer;
  const history = useHistory();
  const [isLinkArtifactModalOpen, setIsLinkArtifactModalOpen] = useState(false);
  const {selectedAccount} = useContext(AccountSelectorContext);
  const hasAutomationAccess = useIsAutomationEnabled(selectedAccount);
  const {
    onStarClick,
    starredRegistries,
    refetch: refetchStarredRegistries,
  } = useContext(StarredRegistriesContext);
  const isStarred = starredRegistries[registry.id] != null;
  const showRegistryStar = useRampFlagStarredRegistries(orgId);

  return (
    <Tailwind style={{width: '100%'}}>
      <div className="flex flex-col sm:flex-row sm:items-center">
        <div className="mb-4 text-2xl font-semibold leading-10 sm:mb-0">
          {capitalizeFirstLetter(registryName)}
        </div>
        <div className="grow" />
        {isLoggedIn && (
          <div className="flex flex-wrap gap-8">
            {showRegistryStar && (
              <Button
                size="large"
                variant="ghost"
                icon={isStarred ? 'star-filled' : 'star'}
                onClick={async e => {
                  e.stopPropagation();
                  try {
                    await onStarClick(
                      registry,
                      !isStarred,
                      'registry homepage'
                    );
                  } catch (err) {
                    toast('Failed to update star for this registry.');
                  }
                }}
              />
            )}
            <Button
              size="large"
              variant="secondary"
              icon="settings"
              tooltip="Settings"
              data-test="registry-settings-button"
              onClick={() => {
                history.replace(
                  urls.globalRegistry(
                    orgName,
                    registryName,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    true
                  )
                );
              }}
            />
            {!readOnly && (
              <>
                <Button
                  className="mr-2"
                  data-test="header-create-automation-button"
                  size="large"
                  variant="secondary"
                  icon="add-new"
                  disabled={!hasAutomationAccess}
                  tooltip={
                    !hasAutomationAccess ? (
                      <PremiumFeatureToolTipText
                        isPremiumFeatureDisabled={true}
                      />
                    ) : undefined
                  }
                  onClick={onCreateAutomation}>
                  <>
                    Create automation
                    <PremiumFeatureTooltip
                      isPremiumFeatureDisabled={!hasAutomationAccess}
                    />
                  </>
                </Button>
                <Button
                  className="header-create-collection"
                  data-test="header-create-collection-button"
                  size="large"
                  variant="secondary"
                  icon="add-new"
                  onClick={onCreateCollection}>
                  Create collection
                </Button>
              </>
            )}
            <Button
              size="large"
              variant="primary"
              icon="add-new"
              onClick={() => {
                setIsLinkArtifactModalOpen(true);
              }}>
              Link artifact
            </Button>
          </div>
        )}
        {isLinkArtifactModalOpen && (
          <LinkArtifactModal
            isLinkArtifactModalOpen={isLinkArtifactModalOpen}
            setIsLinkArtifactModalOpen={setIsLinkArtifactModalOpen}
            entityName={entityName}
            registryProjectName={registryProjectName}
          />
        )}
      </div>
      <div className="mb-16 mt-8 text-base text-moon-750">{description}</div>
      {showJoinBanner ? (
        <RegistryAbleToJoinBanner />
      ) : (
        <>
          <MigrationInfoBanner registry={registry} userID={viewer?.id ?? ''} />
          <SDKVersionWarningBanner
            registry={registry}
            userID={viewer?.id ?? ''}
          />
        </>
      )}
    </Tailwind>
  );
};

const RegistryAbleToJoinBanner = () => {
  return (
    <Banner variant="announcement" className="mt-16">
      <BannerFlexWrapper>
        <Icon name="user-author" style={{marginBottom: 'auto'}} />
        <BannerTextWrapper>
          <BannerBoldText>Available to join.</BannerBoldText>
          As an organization admin you are able to give yourself access to this
          registry. You will be added as a registry admin.
        </BannerTextWrapper>
      </BannerFlexWrapper>
    </Banner>
  );
};

type LinkArtifactModalProps = {
  isLinkArtifactModalOpen: boolean;
  setIsLinkArtifactModalOpen: (isOpen: boolean) => void;
  entityName: string;
  registryProjectName: string;
};

const LinkArtifactModal = ({
  isLinkArtifactModalOpen,
  setIsLinkArtifactModalOpen,
  entityName,
  registryProjectName,
}: LinkArtifactModalProps) => {
  const {sdkVersionValue, setSdkVersionValue} = useSDKVersion();
  const onCopyLinkArtifactClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    copyToClipboard(
      linkArtifactCodeSnippet(entityName, registryProjectName, sdkVersionValue)
    );
    toast('Copied to clipboard');
  };
  return (
    <Tailwind>
      <Dialog.Root
        open={isLinkArtifactModalOpen}
        onOpenChange={setIsLinkArtifactModalOpen}>
        <Dialog.Portal>
          <Dialog.Overlay />
          <Dialog.Content
            className="max-w-[600px] rounded-lg"
            onInteractOutside={e => {
              e.preventDefault();
            }}>
            <Dialog.Title>Link artifact</Dialog.Title>
            <div className="mt-8">
              Use this code snippet to link an artifact to this registry.
            </div>
            <Select
              options={[
                sdkVersionsTab.lowerSdkVersion,
                sdkVersionsTab.higherSdkVersion,
              ]}
              defaultValue={sdkVersionsTab.higherSdkVersion}
              onChange={selected => {
                if (selected != null && selected.value) {
                  setSdkVersionValue(selected.value);
                }
              }}
              className="my-16"
            />
            <div className="mb-16 w-full rounded border border-solid border-moon-250 bg-moon-50 px-12 py-8">
              <span className="flex w-full flex-row">
                <span className="flex-auto whitespace-pre-wrap break-all font-['Inconsolata'] text-sm leading-6">
                  {linkArtifactCodeSnippet(
                    entityName,
                    registryProjectName,
                    sdkVersionValue
                  )}
                </span>
                <span className="ml-8 flex-shrink-0">
                  <Button
                    variant="ghost"
                    size="small"
                    onClick={onCopyLinkArtifactClick}
                    tooltip="Copy to clipboard">
                    Copy
                  </Button>
                </span>
              </span>
            </div>

            <div className="mb-8 flex items-center">
              <Dialog.Close asChild>
                <Button
                  className="dark:text-moon-200/100 dark:filter-none hover:dark:text-moon-200"
                  size="medium"
                  variant="primary">
                  Done
                </Button>
              </Dialog.Close>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </Tailwind>
  );
};
